import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectFlagWindowRemainingMs,
} from '../../redux/features/issueReport/issueReport.selectors';
import {
  closeFlagWindow,
  MAX_FLAG_WINDOW_MS,
} from '../../redux/features/issueReport/issueReport.slice';

export interface IssueFlagWindow {
  issueId: string;
  /** Null while PAUSED — read `remainingMs` instead. */
  expiresAt: number | null;
  /** Banked remainder while paused; null while running. */
  remainingMs: number | null;
  /** True while the agent has focus in the detail field. */
  isPaused: boolean;
}

/**
 * The single owner of the single flag-window timer.
 *
 * `reportCreated` opens the window synchronously in the slice
 * (`created_at + FLAG_WINDOW_MS`) and `voidIssue` closes it, but nothing closes it
 * on EXPIRY — an unattended window would stay open forever. That timer is this hook.
 *
 * Mount it ONCE (the panel does). Two consumers each running their own timeout is
 * exactly how undo and auto-close would drift apart under load.
 *
 * Time remaining is always DERIVED from `expiresAt`, never counted down from a
 * literal, so a re-render, a throttled tab or HackTimer cannot stretch the window.
 *
 * ## Two timers, not one
 *
 * 1. The **window** timer, cleared while paused. Focusing the detail field banks the
 *    remainder and nulls `expiresAt` (see `pauseFlagWindow`), so no close is
 *    scheduled until blur resumes it.
 * 2. The **hard cap**, which runs REGARDLESS of pause state. Without it a
 *    focused-and-abandoned detail field would hold the panel open indefinitely. It is
 *    anchored to `created_at`, not to when the pause began, so pausing repeatedly
 *    cannot walk the deadline forward.
 */
const useIssueFlagWindow = (): IssueFlagWindow | null => {
  const dispatch = useAppDispatch();
  const activeReport = useSelector(selectActiveIssueReport);
  const expiresAt = useSelector(selectActiveFlagWindowExpiresAt);
  const remainingMs = useSelector(selectFlagWindowRemainingMs);

  const issueId = activeReport?.issue_id;
  const createdAt = activeReport?.created_at;
  const isPaused = Boolean(issueId) && expiresAt === null;

  // (1) The window timer. Keyed on window identity, so a re-flag replaces it and a
  // pause (expiresAt -> null) tears it down without scheduling a replacement.
  useEffect(() => {
    if (!expiresAt) return undefined;
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      dispatch(closeFlagWindow());
      return undefined;
    }
    const timer = setTimeout(() => dispatch(closeFlagWindow()), remaining);
    return () => clearTimeout(timer);
  }, [issueId, expiresAt, dispatch]);

  // (2) The hard cap. Deliberately NOT dependent on `expiresAt`, so pausing does not
  // reschedule it.
  useEffect(() => {
    if (!issueId || !createdAt) return undefined;
    const hardRemaining =
      Date.parse(createdAt) + MAX_FLAG_WINDOW_MS - Date.now();
    if (hardRemaining <= 0) {
      dispatch(closeFlagWindow());
      return undefined;
    }
    const timer = setTimeout(() => dispatch(closeFlagWindow()), hardRemaining);
    return () => clearTimeout(timer);
  }, [issueId, createdAt, dispatch]);

  if (!issueId) return null;
  // A paused window is OPEN, just not expiring — so this returns a window even with
  // `expiresAt === null`. Callers must not treat a null expiry as "closed".
  return { issueId, expiresAt, remainingMs, isPaused };
};

export default useIssueFlagWindow;
